'use client';

import { useMemo } from 'react';
import type { SportSpecificFormData } from '@/components/enrollment/SportSpecificForms';

interface SportSpecificDetailsProps {
  sportDetails?: SportSpecificFormData;
  sport?: string; // Optional sport type hint
  theme?: 'light' | 'dark' | 'card';
  className?: string;
}

export function SportSpecificDetails({
  sportDetails,
  sport,
  theme = 'light',
  className = '',
}: SportSpecificDetailsProps) {
  const themeClasses = useMemo(() => {
    switch (theme) {
      case 'dark':
        return {
          container: 'space-y-4 mt-4',
          title: 'font-semibold text-white/90',
          grid: 'grid grid-cols-1 gap-3 sm:grid-cols-2',
          item: 'flex items-center justify-between rounded-lg bg-white/10 px-3 py-2',
          label: 'text-sm text-white/70',
          value: 'text-sm font-medium capitalize text-white',
          textArea: 'rounded-md bg-white/10 px-2 py-1',
          textLabel: 'text-sm font-medium text-white/70',
          textValue: 'mt-1 text-sm text-white',
        };
      case 'card':
        return {
          container: 'space-y-4 mt-4',
          title: 'font-semibold text-gray-900',
          grid: 'grid grid-cols-1 gap-3 sm:grid-cols-2',
          item: 'flex items-center justify-between rounded-lg bg-gradient-to-r from-primary-50/80 to-primary-100/80 px-3 py-2',
          label: 'text-sm text-gray-700',
          value: 'text-sm font-medium capitalize text-gray-900',
          textArea:
            'rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-3',
          textLabel: 'text-sm font-medium text-gray-700',
          textValue: 'mt-1 text-sm text-gray-900',
        };
      default: // light
        return {
          container: 'space-y-4 mt-4',
          title: 'font-semibold text-gray-900',
          grid: 'grid grid-cols-1 gap-3 sm:grid-cols-2',
          item: 'flex items-center justify-between rounded-lg bg-gradient-to-r from-primary-50/80 to-primary-100/80 px-3 py-2',
          label: 'text-sm text-gray-700',
          value: 'text-sm font-medium capitalize text-gray-900',
          textArea:
            'rounded-lg bg-gradient-to-r from-gray-50/80 to-gray-100/80 p-3',
          textLabel: 'text-sm font-medium text-gray-700',
          textValue: 'mt-1 text-sm text-gray-900',
        };
    }
  }, [theme]);

  const renderCricketDetails = () => (
    <div className={themeClasses.container}>
      <h4 className={themeClasses.title}>Cricket Details</h4>
      <div className={themeClasses.grid}>
        {sportDetails?.playerType && (
          <div className={themeClasses.item}>
            <span className={themeClasses.label}>Player Type</span>
            <span className={themeClasses.value}>
              {typeof sportDetails.playerType === 'string'
                ? sportDetails.playerType.replace('-', ' ')
                : sportDetails.playerType}
            </span>
          </div>
        )}
        {sportDetails?.battingStyle && (
          <div className={themeClasses.item}>
            <span className={themeClasses.label}>Batting Style</span>
            <span className={themeClasses.value}>
              {typeof sportDetails.battingStyle === 'string'
                ? sportDetails.battingStyle.replace('-', ' ')
                : sportDetails.battingStyle}
            </span>
          </div>
        )}
        {sportDetails?.bowlingStyle && (
          <div className={themeClasses.item}>
            <span className={themeClasses.label}>Bowling Style</span>
            <span className={themeClasses.value}>
              {typeof sportDetails.bowlingStyle === 'string'
                ? sportDetails.bowlingStyle
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, (c: string) => c.toUpperCase())
                : sportDetails.bowlingStyle}
            </span>
          </div>
        )}
        {sportDetails?.preferredPosition && (
          <div className={themeClasses.item}>
            <span className={themeClasses.label}>Preferred Position</span>
            <span className={themeClasses.value}>
              {sportDetails.preferredPosition}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const renderFootballDetails = () => (
    <div className={themeClasses.container}>
      <h4 className={themeClasses.title}>Football Details</h4>
      <div className={themeClasses.grid}>
        {sportDetails?.position && (
          <div className={themeClasses.item}>
            <span className={themeClasses.label}>Position</span>
            <span className={themeClasses.value}>
              {typeof sportDetails.position === 'string'
                ? sportDetails.position
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, (c: string) => c.toUpperCase())
                : String(sportDetails.position)}
            </span>
          </div>
        )}
        {sportDetails?.preferredFoot && (
          <div className={themeClasses.item}>
            <span className={themeClasses.label}>Preferred Foot</span>
            <span className={themeClasses.value}>
              {sportDetails.preferredFoot}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const renderCommonSportDetails = () => (
    <div className={themeClasses.container}>
      {sportDetails?.specialSkills && (
        <div className={themeClasses.textArea}>
          <span className={themeClasses.textLabel}>Special Skills</span>
          <p className={themeClasses.textValue}>{sportDetails.specialSkills}</p>
        </div>
      )}
      {sportDetails?.playingStyle && (
        <div className={themeClasses.textArea}>
          <span className={themeClasses.textLabel}>Playing Style</span>
          <p className={themeClasses.textValue}>{sportDetails.playingStyle}</p>
        </div>
      )}
    </div>
  );

  if (!sportDetails) return null;

  // Determine sport type from props or by checking available fields
  const sportType =
    sport?.toLowerCase() ||
    (sportDetails.playerType ||
    sportDetails.battingStyle ||
    sportDetails.bowlingStyle
      ? 'cricket'
      : sportDetails.position || sportDetails.preferredFoot
        ? 'football'
        : 'generic');

  const isCricket = sportType === 'cricket';
  const isFootball = sportType === 'football' || sportType === 'soccer';

  return (
    <div className={className}>
      {isCricket && renderCricketDetails()}
      {isFootball && !isCricket && renderFootballDetails()}
      {renderCommonSportDetails()}
    </div>
  );
}
